import type { DeliveryDateCalendarProps } from '@engyalo/delivery-ui-components'
import { CheckoutRules } from '../interfaces'
import type { ICheckoutRuleValue } from '../graphQL/queries/getCheckoutRule/types'
import type { RootState } from '../redux/store'
import { calendarDayInZone } from './calendarToday'
import { readSessionRulePick } from './checkoutRuleSelection'
import { isHidePricesEnabled } from './hidePrices'

/**
 * The calendar props this adapter owns, checked against the library's own prop
 * shape.
 *
 * The same guard the shipped panels use, and the payment picker's sibling. It
 * guards names and value types, not presence — every prop here is optional, so
 * `{}` satisfies it and the colocated assertions cover omission instead.
 *
 * `onDateSelect` is deliberately absent. It is an authored action, bound
 * through `cinnamonActions` to
 * `templateActions.deliveryDate.setDeliveryDate`.
 *
 * `locale`, `previousMonthLabel` and `nextMonthLabel` are absent too, and stay
 * config's. The first is already authorable as `{{config.options.language}}`,
 * and the nav labels are copy.
 */
type InjectedDeliveryDateProps = Pick<
  DeliveryDateCalendarProps,
  'availableDates' | 'selectedDate' | 'hidePrices' | 'referenceDate'
>

/**
 * The wire's options, as the bare day tokens the calendar indexes.
 *
 * ⚠️ **R-8: the token goes in and comes back out untouched.** The storefront
 * re-derives its option list and matches by strict string equality, so a token
 * parsed into a `Date` and re-serialised is rejected for the same day the
 * shopper tapped. Both `'2026-08-13'` and `'2026-08-13T00:00:00.000Z'` are
 * legal inputs and the calendar hands each back in the shape it arrived in.
 * Nothing here may normalise one.
 *
 * `amount` is dropped, as on the payment side; every entry carries `0` today.
 *
 * An entry with no usable `data` is dropped rather than mapped — the array
 * arrives from untyped JSON by way of a checkout rule, so a `null` in it is
 * reachable and `null.data` throws.
 *
 * ⚠️ **The calendar's own drop rules are narrower than this one and it does
 * not export them.** `parseDayToken` rejects anything it cannot place — prose,
 * two-digit years, `'2026-02-30'` — and `indexAvailableDates` then drops
 * duplicates after normalising. So a rule returning five malformed strings is
 * non-empty here and empty on screen, and
 * `checkout.ui.DELIVERY_DATES.emptyHidden` will read `true` over a calendar
 * with nothing to pick. Do not reimplement `parseDayToken` chasing it: it is
 * component-internal on purpose, and a second copy would have to stay in sync
 * across releases.
 */
export const toDeliveryDates = (
  values?: ICheckoutRuleValue[] | null,
): string[] | undefined => {
  if (!Array.isArray(values)) return undefined

  return values
    .filter((value): value is ICheckoutRuleValue => !!value?.data)
    .map((value) => value.data)
}

/**
 * The day to mark: this page load's pick, else the session's saved pick if the
 * rule still offers it (COM-1757). Fresh wins because the snapshot moves only
 * once a write lands (COM-1765); see `selectRulePick` for the full precedence.
 *
 * The fresh pick is forwarded unfiltered: the shopper chose it from this
 * load's list, and the library marks nothing if it has since been withdrawn.
 *
 * ⚠️ **The saved pick is forwarded only when an offered `data` equals it
 * exactly.** A withdrawn day (the rule recomputes from the clock) would
 * otherwise open the calendar on its own month (`resolveInitialMonth` tries
 * `selectedDate` before the earliest offered day) and reach the custom slots
 * as `selectedDate`/`selectedDateLabel` over a grid that marks nothing. And
 * the library normalises tokens, so a saved `'2026-11-02T00:00:00.000Z'` would
 * mark an offered `'2026-11-02'` that auto-settle (`===` on `key ?? data`)
 * and the storefront (R-8) both reject. `===` is auto-settle's rule; never
 * parse or normalise here. No fetch yet means nothing to match, so nothing.
 * `data`, not `key ?? data`: the delivery list is never keyed.
 *
 * After `clearSelection` the saved day is the last *confirmed* one, not the
 * page-load one (COM-1765): a landed write updates the snapshot
 * (`setSessionRulePick`), and `selection_not_offered` recovery drops it
 * (`dropSessionRulePick`), so after a rejection nothing is marked. The cart
 * row and auto-settle read the same snapshot and agree. Three gaps remain:
 *
 * - A write that timed out client-side may have committed, and the snapshot
 *   still shows the value before it.
 * - A catalog refresh (`useRefreshCatalog` → `writeSessionData` →
 *   `setSessionData`) replaces `sessionData` wholesale, so a session read
 *   taken before a write landed can put the older pick back. Not new with
 *   COM-1765.
 * - Any refresh after a recovery re-reads the server, which still holds the
 *   rejected pick until the shopper picks again, so the dropped pick returns
 *   to the snapshot (here it is still marked only if the list offers it).
 */
const toSelectedDate = (
  state: RootState,
  availableDates: string[] | undefined,
): string | undefined => {
  const fresh = state.checkoutSlice.selections[CheckoutRules.DELIVERY_DATES]?.data
  if (typeof fresh === 'string' && fresh !== '') {
    return fresh
  }

  const saved = readSessionRulePick(
    state.defaultSlice.sessionData?.checkoutRules,
    CheckoutRules.DELIVERY_DATES,
  )
  if (!saved || !availableDates?.includes(saved)) {
    return undefined
  }

  return saved
}

/**
 * The four `DeliveryDateCalendar` props cinnamon owns rather than the config
 * author.
 *
 * Injected in `useCinnamonProps` for any component whose `importRoute` is
 * `DeliveryDateCalendar`. None is authorable as a `{{variable}}`: two are
 * shapes the config language cannot build, one is a predicate it cannot
 * compute, and one reads a clock.
 *
 * Everything else stays in config — `deliveryLabel`, `helperText`, the month
 * window, `weekStartsOn`, the colour props, `fieldsToDisplay`, both slots, the
 * nav labels and `onDateSelect`.
 */
export const getDeliveryDatePickerProps = (
  state: RootState,
): InjectedDeliveryDateProps => {
  const availableDates = toDeliveryDates(
    state.checkoutSlice.available[CheckoutRules.DELIVERY_DATES]?.data,
  )

  return {
    availableDates,
    selectedDate: toSelectedDate(state, availableDates),
    /**
     * Which month the calendar opens on, and nothing else.
     *
     * ⚠️ Not a timezone for the dates. The calendar has no `timeZone` prop by
     * design, and pointing a tenant zone at a delivery *day* is the COM-816
     * defect — see `calendarDayInZone`. This anchors the month window only.
     */
    referenceDate: calendarDayInZone(
      state.defaultSlice.sessionData?.configuration?.i18n?.timezone,
    ),
    /**
     * The calendar renders no money — a date grid holds no amount — so this is
     * for the tenant strings. Both custom slots can reach any field on the
     * source object, and `maskPricesForDisplay` is an allowlist of prop *names*
     * over *numbers*.
     */
    hidePrices: isHidePricesEnabled(state),
  }
}

export default getDeliveryDatePickerProps
